// Plan and billing (docs/cockpit/spec.md, Setup group; ADR-0016, #212): the Plan, the period paid for and the next
// charge; people active this month and GST Registrations against the Plan's allowance; Credits included, bought and
// used; payment history; and subscribing, buying a Credit pack or cancelling. Only Owners see it, and only the Account
// Holder subscribes or cancels. Paying is a high-stakes action: the person confirms the amount with GST on a preview
// before Razorpay's own page opens. The Application API checks every action again. LAMP's GST tax invoice for each
// payment is listed with its PDF, and the Account Holder chooses what those invoices carry: which of the business's
// Registrations is billed, or none (ADR-0029, ToS 6.2).
import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  rupees,
  type Api,
  type BillingCheckout,
  type BillingPage,
  type InvoiceParticulars,
  type Quote,
} from "../api.js";
import { formatDate, formatNumber, locale } from "../i18n/format.js";
import type { Language } from "../i18n/language.js";
import { useLanguage, useWords, type Words } from "../i18n/words.js";

const MILLI = 1000;
const number = (value: number, language: Language) => formatNumber(value, language, { maximumFractionDigits: 1 });
/** A calendar date (`YYYY-MM-DD`) in the person's language. */
const day = (date: string, language: Language) =>
  formatDate(`${date.slice(0, 10)}T00:00:00Z`, language, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
const monthName = (month: string, language: Language) =>
  new Intl.DateTimeFormat(locale(language), { month: "long", year: "numeric", timeZone: "UTC" }).format(
    new Date(`${month}-01T00:00:00Z`),
  );

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError
    ? error.problems.length > 0
      ? error.problems.join("; ")
      : w.ownerPagesRefused(error.code)
    : w.ownerPagesUnreachable;

/** Something to pay for, as the preview shows it before Razorpay's page opens. */
interface Choice {
  readonly what: string;
  readonly quote: Quote;
  readonly pay: () => Promise<BillingCheckout>;
}

/** The amount with GST, confirmed before anything is charged. */
function Preview({
  choice,
  busy,
  onPay,
  onBack,
}: {
  choice: Choice;
  busy: boolean;
  onPay: () => void;
  onBack: () => void;
}) {
  const w = useWords();
  return (
    <div className="card" role="group" aria-label={w.billingConfirm}>
      <p>
        <strong>{choice.what}</strong>
      </p>
      <dl className="facts">
        <dt>{w.billingPrice}</dt>
        <dd>{rupees(choice.quote.pricePaise)}</dd>
        <dt>{w.billingGst}</dt>
        <dd>{rupees(choice.quote.gstPaise)}</dd>
        <dt>{w.billingTotal}</dt>
        <dd>
          <strong>{rupees(choice.quote.totalPaise)}</strong>
        </dd>
      </dl>
      <button type="button" className="btn btn-go" disabled={busy} onClick={onPay}>
        {w.billingPay(rupees(choice.quote.totalPaise))}
      </button>{" "}
      <button type="button" className="btn" disabled={busy} onClick={onBack}>
        {w.billingBack}
      </button>
    </div>
  );
}

/** The Account Holder's choice of what LAMP's invoices carry: a Registration of the business, or none. */
function InvoiceTo({
  page,
  busy,
  onSave,
}: {
  page: BillingPage;
  busy: boolean;
  onSave: (choice: Parameters<Api["chooseInvoiceParticulars"]>[0]) => void;
}) {
  const w = useWords();
  // A mistyped GSTIN is shown as needing correction above, never offered: choosing it would be refused.
  const registrations = page.invoiceParticulars.registrations.filter((r) => !r.needsCorrecting);
  const { current } = page.invoiceParticulars;
  const first = registrations[0];
  const offered = (value: string | null | undefined) =>
    value && registrations.some((r) => r.gstin === value) ? value : "";
  const [gstin, setGstin] = useState<string>(current ? offered(current.gstin) : (first?.gstin ?? ""));
  const [legalName, setLegalName] = useState(current?.legalName ?? first?.legalName ?? "");
  const [address, setAddress] = useState(current?.address ?? first?.address ?? "");
  const [state, setState] = useState(current?.state ?? "");
  const pick = (value: string) => {
    setGstin(value);
    const registration = registrations.find((r) => r.gstin === value);
    if (registration) {
      setLegalName(registration.legalName);
      if (registration.address) setAddress(registration.address);
    }
  };
  return (
    <form
      className="card"
      aria-label={w.billingInvoiceToChange}
      onSubmit={(event) => {
        event.preventDefault();
        onSave(gstin === "" ? { gstin: null, legalName, address, state } : { gstin, legalName, address });
      }}
    >
      <label>
        {w.billingInvoiceToRegistration}{" "}
        <select value={gstin} onChange={(event) => pick(event.target.value)}>
          {registrations.map((r) => (
            <option key={r.gstin} value={r.gstin}>
              {`${r.gstin} (${r.legalName})`}
            </option>
          ))}
          <option value="">{w.billingInvoiceToNoGstin}</option>
        </select>
      </label>
      <label>
        {w.billingInvoiceToName}{" "}
        <input required maxLength={200} value={legalName} onChange={(event) => setLegalName(event.target.value)} />
      </label>
      <label>
        {w.billingInvoiceToAddress}{" "}
        <input required maxLength={500} value={address} onChange={(event) => setAddress(event.target.value)} />
      </label>
      {gstin === "" ? (
        <label>
          {w.billingInvoiceToState}{" "}
          <input
            required
            inputMode="numeric"
            pattern="[0-9]{2}"
            maxLength={2}
            value={state}
            onChange={(event) => setState(event.target.value)}
          />
        </label>
      ) : null}
      <button type="submit" className="btn" disabled={busy}>
        {w.billingInvoiceToSave}
      </button>
    </form>
  );
}

const particularsLine = (p: InvoiceParticulars, w: Words) => w.billingInvoiceToLine(p.legalName, p.address, p.gstin);

function statusLine(page: BillingPage, w: Words): string | undefined {
  if (page.status === "charge_failed") return w.billingChargeFailed;
  if (page.status === "halted") return w.billingHalted;
  if (page.status === "cancelled") return w.billingCancelledStatus;
  return undefined;
}

export function Billing({
  api,
  open = (url) => window.location.assign(url),
}: {
  api: Api;
  /** Opens Razorpay's page to pay on. */
  open?: (url: string) => void;
}) {
  const w = useWords();
  const { language } = useLanguage();
  const [page, setPage] = useState<BillingPage | undefined>(undefined);
  const [choice, setChoice] = useState<Choice | undefined>(undefined);
  const [cancelling, setCancelling] = useState(false);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () => api.billing().then(setPage, (error: unknown) => setProblem(problemOf(error, w))),
    [api, w],
  );
  useEffect(() => {
    void load();
  }, [api]);

  const attempt = async (work: () => Promise<string | undefined>) => {
    setBusy(true);
    setNotice(undefined);
    setProblem(undefined);
    try {
      const done = await work();
      if (done !== undefined) setNotice(done);
      await load();
    } catch (error) {
      setProblem(problemOf(error, w));
    } finally {
      setBusy(false);
      setChoice(undefined);
      setCancelling(false);
    }
  };

  const pay = (chosen: Choice) =>
    attempt(async () => {
      const made = await chosen.pay();
      if (made.url !== null) {
        open(made.url);
        return undefined;
      }
      return w.billingPaid;
    });

  if (!page) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.billingLoading}</p>;
  const takesPayments = page.provider !== "none";
  const status = statusLine(page, w);
  return (
    <>
      <p className="muted">{w.billingIntro}</p>
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      {page.readOnly.includes("payment_overdue") ? (
        <p className="alert" role="alert">
          {w.billingReadOnly}
        </p>
      ) : page.overdueSince ? (
        <p className="alert" role="alert">
          {w.billingOverdue(day(page.overdueSince, language))}
        </p>
      ) : null}

      <section className="section" aria-labelledby="billing-plan">
        <h2 id="billing-plan">{w.billingYourPlan}</h2>
        {page.plan ? (
          <>
            <p>
              <strong>{w.billingPlanLine(page.plan.name, page.plan.billingPeriod === "annual")}</strong>
            </p>
            <p>{w.billingPaidFor(day(page.plan.periodStart, language), day(page.plan.periodEnd, language))}</p>
            {page.nextChargeOn ? <p>{w.billingNextCharge(day(page.nextChargeOn, language))}</p> : null}
          </>
        ) : (
          <p className="muted">{w.billingNoPlan}</p>
        )}
        {status ? <p role="note">{status}</p> : null}
        {page.cancellable && page.accountHolder && takesPayments ? (
          cancelling ? (
            <div className="card" role="group" aria-label={w.billingCancelPlan}>
              <p>{w.billingCancelQuestion}</p>
              <button
                type="button"
                className="btn btn-danger"
                disabled={busy}
                onClick={() =>
                  void attempt(async () => {
                    const { until } = await api.cancelSubscription();
                    return w.billingCancelled(until === null ? undefined : day(until, language));
                  })
                }
              >
                {w.billingCancelYes}
              </button>{" "}
              <button type="button" className="btn" disabled={busy} onClick={() => setCancelling(false)}>
                {w.billingBack}
              </button>
            </div>
          ) : (
            <button type="button" className="btn" disabled={busy} onClick={() => setCancelling(true)}>
              {w.billingCancelPlan}
            </button>
          )
        ) : null}
      </section>

      <section className="section" aria-labelledby="billing-month">
        <h2 id="billing-month">{w.billingThisMonth}</h2>
        <ul className="plain-list">
          <li>{w.billingPeople(number(page.people.active, language), number(page.people.included, language))}</li>
          <li>
            {w.billingRegistrations(
              number(page.registrations.count, language),
              number(page.registrations.included, language),
            )}
          </li>
          <li>
            {w.billingCreditsLine(
              number(page.credits.usedMilliCredits / MILLI, language),
              number(page.credits.includedMilliCredits / MILLI, language),
              number(page.credits.boughtMilliCredits / MILLI, language),
            )}
          </li>
          {page.addOns.map((a) => (
            <li key={a.month}>
              {w.billingExtraPeople(
                number(a.quantity, language),
                monthName(a.month, language),
                rupees(a.unitPricePaise),
              )}
            </li>
          ))}
        </ul>
      </section>

      {choice ? (
        <Preview choice={choice} busy={busy} onPay={() => void pay(choice)} onBack={() => setChoice(undefined)} />
      ) : !takesPayments ? (
        <p className="muted">{w.billingNoPayments}</p>
      ) : (
        <>
          <section className="section" aria-labelledby="billing-plans">
            <h2 id="billing-plans">{w.billingChoosePlan}</h2>
            {!page.accountHolder ? (
              <p className="muted">{w.billingOnlyHolder}</p>
            ) : page.planCheckoutRefused ? (
              <p className="muted">{page.planCheckoutRefused}</p>
            ) : (
              <ul className="plain-list">
                {page.offers.plans.map((p) => {
                  const offer = w.billingPlanOffer(
                    p.name,
                    number(p.includedPeople, language),
                    number(p.includedCredits, language),
                  );
                  return (
                    <li key={p.id}>
                      {offer}{" "}
                      <button
                        type="button"
                        className="btn"
                        disabled={busy}
                        onClick={() =>
                          setChoice({
                            what: w.billingPlanLine(p.name, false),
                            quote: p.monthly,
                            pay: () => api.subscribe(p.id, "monthly", p.monthly.totalPaise),
                          })
                        }
                      >
                        {w.billingMonthly(rupees(p.monthly.totalPaise))}
                      </button>{" "}
                      <button
                        type="button"
                        className="btn"
                        disabled={busy}
                        onClick={() =>
                          setChoice({
                            what: w.billingPlanLine(p.name, true),
                            quote: p.annual,
                            pay: () => api.subscribe(p.id, "annual", p.annual.totalPaise),
                          })
                        }
                      >
                        {w.billingAnnual(rupees(p.annual.totalPaise))}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
          <section className="section" aria-labelledby="billing-credits">
            <h2 id="billing-credits">{w.billingBuyCredits}</h2>
            <ul className="plain-list">
              {page.offers.packs.map((p) => {
                const what = w.billingPackOffer(number(p.credits, language), rupees(p.quote.totalPaise));
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      className="btn"
                      disabled={busy}
                      onClick={() =>
                        setChoice({ what, quote: p.quote, pay: () => api.buyCreditPack(p.id, p.quote.totalPaise) })
                      }
                    >
                      {what}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        </>
      )}

      <section className="section" aria-labelledby="billing-history">
        <h2 id="billing-history">{w.billingHistory}</h2>
        {page.payments.length === 0 ? (
          <p className="muted">{w.billingNoHistory}</p>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th scope="col">{w.billingWhen}</th>
                  <th scope="col">{w.billingWhat}</th>
                  <th scope="col">{w.billingAmount}</th>
                  <th scope="col">{w.billingReference}</th>
                </tr>
              </thead>
              <tbody>
                {page.payments.map((p) => (
                  <tr key={`${p.kind}-${p.reference}`}>
                    <td>
                      {formatDate(p.at, language, {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        timeZone: "Asia/Kolkata",
                      })}
                    </td>
                    <td>{p.what}</td>
                    <td>
                      {p.kind === "subscription_charge_failed"
                        ? w.billingFailedCharge
                        : p.amountPaise === null
                          ? ""
                          : rupees(p.amountPaise)}
                    </td>
                    <td>{p.reference}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* A Cell that takes no payments issues no invoices. */}
      {takesPayments ? (
        <>
          <section className="section" aria-labelledby="billing-invoices">
            <h2 id="billing-invoices">{w.billingInvoices}</h2>
            {page.invoices.length === 0 ? (
              <p className="muted">{w.billingNoInvoices}</p>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th scope="col">{w.billingInvoiceNumber}</th>
                      <th scope="col">{w.billingWhen}</th>
                      <th scope="col">{w.billingAmount}</th>
                      <th scope="col">{w.billingInvoicePdf}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.invoices.map((i) => (
                      <tr key={i.id}>
                        <td>{i.number}</td>
                        <td>{day(i.date, language)}</td>
                        <td>{rupees(i.totalPaise)}</td>
                        <td>
                          <a href={i.pdf} target="_blank" rel="noreferrer">
                            {w.billingInvoiceOpen(i.number)}
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="section" aria-labelledby="billing-invoice-to">
            <h2 id="billing-invoice-to">{w.billingInvoiceTo}</h2>
            <p>
              {page.invoiceParticulars.current
                ? particularsLine(page.invoiceParticulars.current, w)
                : w.billingInvoiceToNone}
            </p>
            {page.invoiceParticulars.registrations
              .filter((r) => r.needsCorrecting)
              .map((r) => (
                <p key={r.gstin} className="alert" role="alert">
                  {w.billingGstinNeedsCorrecting(r.gstin)} <a href="/settings">{w.billingGstinCorrectIn}</a>
                </p>
              ))}
            {page.accountHolder ? (
              <InvoiceTo
                page={page}
                busy={busy}
                onSave={(chosen) =>
                  void attempt(async () => {
                    await api.chooseInvoiceParticulars(chosen);
                    return w.billingInvoiceToSaved;
                  })
                }
              />
            ) : (
              <p className="muted">{w.billingInvoiceToHolder}</p>
            )}
          </section>
        </>
      ) : null}
    </>
  );
}
